import { Spinner } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { Waypoint } from 'react-waypoint';

import { auditLogsApi } from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './activityLog.module.scss';
import { ComponentByLogType } from './activityTypes';
/**
 * Represents a component that outputs the log entries for the main
 * conversation.
 */
export function ActivityLog(): JSX.Element {
  const {
    metaConversation: { conversation, messages },
  } = useAppSelector(
    (state) => ({
      metaConversation: state.conversation.metaConversation,
    }),
    shallowEqual,
  );

  const {
    items: entries,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readConversationAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  useEffect(() => {
    if (conversation?.id) {
      changeArg({
        resourceId: conversation.id.toString(),
        limit: 1000,
        sort: { key: 'timestamp', direction: 'asc' },
      });
    }
  }, [changeArg, conversation.id]);

  return (
    <div
      className={classnames({
        [styles.root]: true,
        [styles.hasLogs]: entries.length,
      })}
    >
      {asyncStatus === 'rejected' && (
        <span className={styles.error}>Failed to retrieve Activity Log.</span>
      )}

      {entries.map((entry) => (
        <ComponentByLogType
          key={entry.event_id}
          entry={entry}
          messages={messages}
        />
      ))}
      {asyncStatus === 'fulfilled' &&
        !entries.length &&
        'No events to display.'}

      {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') && (
        <div className={styles.spinnerContainer} data-testid="spinner">
          <Spinner />
        </div>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </div>
  );
}
